import * as React from "react";

/**
 * useAutoToastOnLevelChange — feuert ein custom-event wenn die aktuelle
 * hitzestufe sich strict-greater-than gegenüber dem vorherigen render erhöht.
 *
 * Throttle: max 1 toast pro 4h per org-ID (localStorage-key
 * `hp.auto-toast.last-fired.<orgId>`, ISO-timestamp). LocalStorage-fehler
 * (private-browsing, quota, deaktiviert) → fail-open: toast erscheint
 * trotzdem (sicherer als unterdrücken bei stufe-erhöhung).
 *
 * Initial-mount feuert NICHT — sonst würde jedes dashboard-mount mit
 * level ≥ 2 einen toast werfen (page-reload, route-rückkehr). Erst echter
 * stufen-wechsel innerhalb derselben session triggert.
 *
 * Event-pattern konsistent mit UpdateAvailableToast (sw-update-event):
 * window-custom-event `hitzeprotokoll:level-rise` mit detail-payload.
 */
const THROTTLE_MS = 4 * 60 * 60 * 1000;
const STORAGE_KEY_PREFIX = "hp.auto-toast.last-fired.";

export interface LevelRiseEventDetail {
  level: number;
  orgId: string;
}

function storageKey(orgId: string): string {
  return `${STORAGE_KEY_PREFIX}${orgId}`;
}

function readLastFired(orgId: string): number | null {
  try {
    const raw = window.localStorage.getItem(storageKey(orgId));
    if (!raw) return null;
    const ts = Date.parse(raw);
    return Number.isFinite(ts) ? ts : null;
  } catch {
    return null;
  }
}

function writeLastFired(orgId: string, isoTimestamp: string): void {
  try {
    window.localStorage.setItem(storageKey(orgId), isoTimestamp);
  } catch {
    // fail-silent — toast funktioniert auch ohne persistenz, nur ohne throttle
  }
}

export function useAutoToastOnLevelChange(
  currentLevel: number,
  orgId: string | undefined,
): void {
  const prevLevelRef = React.useRef<number | null>(null);

  React.useEffect(() => {
    if (!orgId) return;
    if (prevLevelRef.current === null) {
      prevLevelRef.current = currentLevel;
      return;
    }
    if (currentLevel > prevLevelRef.current) {
      const last = readLastFired(orgId);
      const now = Date.now();
      const allowed = last === null || now - last >= THROTTLE_MS;
      if (allowed) {
        window.dispatchEvent(
          new CustomEvent<LevelRiseEventDetail>("hitzeprotokoll:level-rise", {
            detail: { level: currentLevel, orgId },
          }),
        );
        writeLastFired(orgId, new Date(now).toISOString());
      }
    }
    prevLevelRef.current = currentLevel;
  }, [currentLevel, orgId]);
}
